import type { IAssetTemplateModalData } from '../modules/modal-pick-template/types';

import { IAssetTemplateModalType } from '../modules/modal-pick-template/types';
import { findVariantByOrigin } from '~/modules/campaign/utils';
import { useCallback, useMemo, useState } from 'react';

interface IModalPickInfo {
  modalTitle: string;
  primaryActionContent: string;
}
export interface IAssetTemplateModalDataResult {
  modalType?: IAssetTemplateModalType;
  modalData?: IAssetTemplateModalData;
  isPickVariant: boolean;
  isPickControl: boolean;
  isOpenModal: boolean;
  isOpenModalMultiple: boolean;
  modalPickInfo?: IModalPickInfo;
  handleCloseModal: () => void;
  handleSetModalType: (value: IAssetTemplateModalType) => void;
  handleSetModalData: (value: IAssetTemplateModalData) => void;
}
export const useInitializeAssetTemplateModalData = (): IAssetTemplateModalDataResult => {
  const [modalType, setModalType] = useState<IAssetTemplateModalType | undefined>(undefined);
  const [modalData, setModalData] = useState<IAssetTemplateModalData | undefined>(undefined);

  const isPickControl = modalType === IAssetTemplateModalType.VARIANT_A;
  const isOpenModalMultiple = useMemo(
    () => modalType === IAssetTemplateModalType.MULTIPLE_VARIANT_A,
    [modalType],
  );
  const isPickVariant = useMemo(() => {
    if (!modalType || !modalData) return false;

    const variantB = findVariantByOrigin(modalData.campaign, false);
    if (isPickControl && !!variantB?.templateSuffix) return true;

    return [IAssetTemplateModalType.VARIANT_B].includes(modalType);
  }, [modalData, modalType, isPickControl]);

  const isOpenModal = useMemo(() => !!modalType, [modalType]);
  const modalPickInfo = useMemo(() => {
    if (!modalType) return;

    const titles: Record<IAssetTemplateModalType, string> = {
      [IAssetTemplateModalType.MULTIPLE_VARIANT_A]: 'Select templates for Control group',
      [IAssetTemplateModalType.VARIANT_A]: 'Select a template for Control',
      [IAssetTemplateModalType.VARIANT_B]: 'Select a template for Variant',
    };
    const actionContents: Record<IAssetTemplateModalType, string> = {
      [IAssetTemplateModalType.MULTIPLE_VARIANT_A]: 'Select templates',
      [IAssetTemplateModalType.VARIANT_A]: 'Select template',
      [IAssetTemplateModalType.VARIANT_B]: 'Select template',
    };

    return {
      modalTitle: titles[modalType],
      primaryActionContent: actionContents[modalType],
    };
  }, [modalType]);

  const handleSetModalType = useCallback(
    (value: IAssetTemplateModalType) => setModalType(value),
    [],
  );
  const handleSetModalData = useCallback(
    (value: IAssetTemplateModalData) => setModalData(value),
    [],
  );
  const handleCloseModal = useCallback(() => {
    setModalType(undefined);
    setModalData(undefined);
  }, []);

  return {
    modalType,
    modalData,
    isPickControl,
    isOpenModal,
    isPickVariant,
    modalPickInfo,
    isOpenModalMultiple,
    handleSetModalType,
    handleCloseModal,
    handleSetModalData,
  };
};
